/**
 * EdgToolbar — the EDG panel-header tool buttons (phase 6c, step 2).
 *
 * Direct port of `EDG.toolbarButtons` (components/EDG/index.js:356) — the armed
 * tools that drive the EDG `ui` store: add-object, add-dependency (with the
 * cardinality dropdown), add-inheritance, show-BUL, show-TDL, and the two dagre
 * auto-layout buttons. The export-PNG button is intentionally LEFT OUT here; PNG
 * export is phase 6e.
 *
 * The buttons read + write the SAME observable `ui` store the old EDG used
 * (`ui.activeTool`, `ui.activeDependencyType`, `ui.toggleTool`,
 * `ui.activateDependencyType`), so arming a tool flips `activeTool` exactly as
 * before — which is what EdgFlow's node/edge/pane/connect gating keys off. The
 * auto-layout buttons call the `onLayout` prop (wired by the caller to the dagre
 * layout + the existing position commits as one undoable step).
 *
 * Reuses the SAME `Toolbar` UI component (variant="panel") + the SAME icons the
 * old EDG header used, so the control looks identical. At cutover (step 3) this
 * toolbar moves into ProjectVersion's EDG `Panel` header (where the old
 * `renderHeader` rendered it); on the dev surface it lets the armed-tool flows be
 * driven end-to-end.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { observer } from 'mobx-react';
import Toolbar from '../components/Toolbar';
import {
  AddClass as AddClassIcon,
  DependencyOptionalN as DependencyOptionalNIcon,
  DependencyOptional1 as DependencyOptional1Icon,
  DependencyMandatoryN as DependencyMandatoryNIcon,
  DependencyMandatory1 as DependencyMandatory1Icon,
  LayoutBUL as LayoutBULIcon,
  LayoutTDL as LayoutTDLIcon,
  ShowBUL as ShowBULIcon,
  ShowTDL as ShowTDLIcon,
  Picture as PictureIcon,
  Inheritance as InheritanceIcon,
} from '../components/Icons';

// The four dependency-type dropdown buttons (port of `dependencyIcons` /
// `dependencyTitles` in components/EDG/index.js).
const dependencyIcons = {
  OPTIONAL_N: DependencyOptionalNIcon,
  OPTIONAL_1: DependencyOptional1Icon,
  MANDATORY_N: DependencyMandatoryNIcon,
  MANDATORY_1: DependencyMandatory1Icon,
} as const;

const dependencyTitles = {
  OPTIONAL_N: 'Optional N',
  OPTIONAL_1: 'Optional 1',
  MANDATORY_N: 'Mandatory N',
  MANDATORY_1: 'Mandatory 1',
} as const;

type DependencyTypeKey = keyof typeof dependencyIcons;

export type EdgToolbarProps = {
  /** The EDG UI store (`store.ui.edg`) — the SAME observable the old EDG read. */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any;
  /** Run a dagre auto-layout in the given direction (BUL → BT, TDL → TD). */
  onLayout: (direction: 'BT' | 'TD') => void;
  /**
   * Start the PNG export (the old EDG's "Save image" button → the existing
   * `ui.project.setExportEDGImage(true)` flow, consumed by ProjectActions /
   * ExportImageModal). PNG export itself is 6e scope, but the trigger button
   * must stay in the EDG header so users can still launch it. Omitted on the dev
   * surface (no ProjectActions there), where no export button is shown.
   */
  onExportImage?: () => void;
  /** Extra class for the toolbar wrapper. */
  className?: string;
};

const EdgToolbar = observer(function EdgToolbar({
  ui,
  onLayout,
  onExportImage,
  className,
}: EdgToolbarProps) {
  const activeDependencyType: DependencyTypeKey = ui.activeDependencyType ?? 'OPTIONAL_N';

  // Port of `toggleButton` — a tool button that toggles `ui.activeTool`.
  const toggleButton = (
    key: string,
    icon: unknown,
    title: string,
    dropdown?: unknown
  ) => ({
    key,
    icon,
    title,
    handler: () => ui.toggleTool(key),
    active: ui.activeTool === key,
    dropdown,
  });

  // Port of `dependencyTypeButton` — a dropdown entry that arms add-dependency
  // with a specific cardinality.
  const dependencyTypeButton = (key: DependencyTypeKey) => ({
    key,
    title: dependencyTitles[key],
    icon: dependencyIcons[key],
    active: ui.activeDependencyType === key,
    handler: () => ui.activateDependencyType(key),
  });

  const buttons = [
    toggleButton('add_object', AddClassIcon, 'Add object'),
    toggleButton('add_dependency', dependencyIcons[activeDependencyType], 'Add dependency', {
      arrow: true,
      buttons: (Object.keys(dependencyIcons) as DependencyTypeKey[]).map(dependencyTypeButton),
    }),
    toggleButton('add_inheritance', InheritanceIcon, 'Add inheritance'),
    toggleButton('show_bul', ShowBULIcon, 'Show Bottom Up Level'),
    toggleButton('show_tdl', ShowTDLIcon, 'Show Top Down Level'),
    {
      key: 'layout_bul',
      icon: LayoutBULIcon,
      title: 'Auto-layout Bottom Up',
      handler: () => onLayout('BT'),
      active: false,
    },
    {
      key: 'layout_tdl',
      icon: LayoutTDLIcon,
      title: 'Auto-layout Top Down',
      handler: () => onLayout('TD'),
      active: false,
    },
    // The "Save image" button — last in the old EDG header. Only rendered when an
    // `onExportImage` handler is supplied (the real project layout); the dev
    // surface omits it.
    ...(onExportImage
      ? [
          {
            key: 'export picture',
            icon: PictureIcon,
            title: 'Save image',
            handler: onExportImage,
            active: false,
          },
        ]
      : []),
  ];

  return <Toolbar variant="panel" buttons={buttons} className={className} />;
});

export default EdgToolbar;
